/**
 * Security Settings Page - Epic 87
 * Centralized security management including MFA TOTP setup
 * Carbon Design System compliant
 */

import React, { useState, useEffect } from 'react';
import {
  Content,
  Grid,
  Column,
  Heading,
  Stack,
  Button,
  Tile,
  Toggle,
  InlineNotification,
  Loading,
  Tag,
} from '@carbon/react';
import { Security, Locked, Unlocked, PhoneIp, Events, Password, CheckmarkFilled } from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';
import MFASetup from '../../components/auth/MFASetup';
import styles from './SecuritySettingsPage.module.scss';

interface MFAStatus {
  mfa_enabled: boolean;
  mfa_method: string | null;
  security_score: number;
  backup_codes_remaining: number;
}

interface SecuritySettings {
  mfa: MFAStatus;
  password_last_changed: number;
  active_sessions_count: number;
}

const SecuritySettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [settings, setSettings] = useState<SecuritySettings | null>(null);
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userId, setUserId] = useState<string>('');

  const API_BASE_URL = process.env.VITE_API_BASE_URL || '/api';

  useEffect(() => {
    loadSecuritySettings();
  }, []);

  const loadSecuritySettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        navigate('/auth/login');
        return;
      }

      // Load user profile to get email and user_id
      const profileResponse = await fetch(`${API_BASE_URL}/api/v1/auth/profile`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setUserEmail(profileData.data?.email || profileData.data?.profile?.email || '');
        setUserId(profileData.data?.user_id || profileData.data?.profile?.user_id || '');
      }

      // Load security settings
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/security/settings`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('auth_token');
          navigate('/auth/login');
          return;
        }

        // If endpoint doesn't exist yet, use mock data
        if (response.status === 404) {
          setSettings({
            mfa: {
              mfa_enabled: false,
              mfa_method: null,
              security_score: 25,
              backup_codes_remaining: 0,
            },
            password_last_changed: Date.now() - 30 * 24 * 60 * 60 * 1000, // 30 days ago
            active_sessions_count: 1,
          });
          setLoading(false);
          return;
        }

        throw new Error('Failed to load security settings');
      }

      const data = await response.json();
      setSettings(data.data || data);
    } catch (err: any) {
      setError(err.message || 'Failed to load security settings');

      // Set default values on error
      setSettings({
        mfa: {
          mfa_enabled: false,
          mfa_method: null,
          security_score: 25,
          backup_codes_remaining: 0,
        },
        password_last_changed: Date.now() - 30 * 24 * 60 * 60 * 1000,
        active_sessions_count: 1,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSetupComplete = (success: boolean) => {
    if (success) {
      setSuccess('Two-factor authentication enabled successfully!');
      loadSecuritySettings(); // Reload settings to update MFA status

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    }
    setMfaSetupOpen(false);
  };

  const getSecurityScoreColor = (score: number): 'red' | 'magenta' | 'green' => {
    if (score < 40) return 'red';
    if (score < 70) return 'magenta';
    return 'green';
  };

  const getSecurityScoreLabel = (score: number): string => {
    if (score < 40) return 'Weak';
    if (score < 70) return 'Moderate';
    return 'Strong';
  };

  if (loading) {
    return (
      <Content className={styles.securitySettingsPage}>
        <Loading description="Loading security settings..." withOverlay={false} />
      </Content>
    );
  }

  return (
    <Content className={styles.securitySettingsPage}>
      <Grid>
        <Column lg={16} md={8} sm={4}>
          <Stack gap={6}>
            {/* Page Header */}
            <div className={styles.pageHeader}>
              <Stack gap={4}>
                <Heading className={styles.pageTitle}>Security Settings</Heading>
                <p className={styles.pageDescription}>
                  Manage your account security settings, two-factor authentication, and active sessions.
                </p>
              </Stack>
            </div>

            {/* Notifications */}
            {error && (
              <InlineNotification
                kind="error"
                title="Error"
                subtitle={error}
                lowContrast
                onCloseButtonClick={() => setError(null)}
              />
            )}

            {success && (
              <InlineNotification
                kind="success"
                title="Success"
                subtitle={success}
                lowContrast
                onCloseButtonClick={() => setSuccess(null)}
              />
            )}

            {/* Security Score Overview */}
            {settings && (
              <Tile className={styles.securityScoreTile}>
                <Grid>
                  <Column lg={4} md={2} sm={4}>
                    <div className={styles.scoreIcon}>
                      <Security size={48} />
                    </div>
                  </Column>
                  <Column lg={12} md={6} sm={4}>
                    <Stack gap={3}>
                      <div>
                        <h3 className={styles.scoreTileHeading}>Account Security Score</h3>
                        <div className={styles.scoreDisplay}>
                          <span className={styles.scoreValue}>{settings.mfa.security_score}/100</span>
                          <Tag type={getSecurityScoreColor(settings.mfa.security_score)} size="md">
                            {getSecurityScoreLabel(settings.mfa.security_score)}
                          </Tag>
                        </div>
                      </div>
                      <p className={styles.scoreDescription}>
                        {settings.mfa.mfa_enabled
                          ? 'Your account has strong security with two-factor authentication enabled.'
                          : 'Enable two-factor authentication to significantly improve your account security.'}
                      </p>
                    </Stack>
                  </Column>
                </Grid>
              </Tile>
            )}

            {/* Two-Factor Authentication */}
            <Tile className={styles.securitySection}>
              <Stack gap={5}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionIcon}>
                    {settings?.mfa.mfa_enabled ? (
                      <Locked size={24} className={styles.iconEnabled} />
                    ) : (
                      <Unlocked size={24} className={styles.iconDisabled} />
                    )}
                  </div>
                  <div>
                    <h3 className={styles.sectionHeading}>Two-Factor Authentication (2FA)</h3>
                    <p className={styles.sectionDescription}>
                      Add an extra layer of security to your account with authenticator app codes.
                    </p>
                  </div>
                </div>

                {settings?.mfa.mfa_enabled ? (
                  <div className={styles.mfaEnabledContent}>
                    <InlineNotification
                      kind="success"
                      title="2FA Enabled"
                      subtitle={`Using ${settings.mfa.mfa_method?.toUpperCase() || 'TOTP'} authentication. ${
                        settings.mfa.backup_codes_remaining || 0
                      } backup codes remaining.`}
                      lowContrast
                      hideCloseButton
                      className={styles.mfaStatusNotification}
                    />

                    <Stack gap={3} orientation="horizontal">
                      <Button kind="tertiary" size="md" renderIcon={Password}>
                        Regenerate Backup Codes
                      </Button>
                      <Button kind="danger--tertiary" size="md" renderIcon={Unlocked}>
                        Disable 2FA
                      </Button>
                    </Stack>
                  </div>
                ) : (
                  <div className={styles.mfaDisabledContent}>
                    <InlineNotification
                      kind="warning"
                      title="2FA Not Enabled"
                      subtitle="Your account is vulnerable without two-factor authentication. We strongly recommend enabling it."
                      lowContrast
                      hideCloseButton
                      className={styles.mfaStatusNotification}
                    />

                    <Button
                      kind="primary"
                      size="md"
                      renderIcon={Locked}
                      onClick={() => setMfaSetupOpen(true)}
                      data-testid="enable-mfa-button"
                    >
                      Enable Two-Factor Authentication
                    </Button>
                  </div>
                )}
              </Stack>
            </Tile>

            {/* Active Sessions */}
            <Tile className={styles.securitySection}>
              <Stack gap={5}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionIcon}>
                    <PhoneIp size={24} />
                  </div>
                  <div>
                    <h3 className={styles.sectionHeading}>Active Sessions</h3>
                    <p className={styles.sectionDescription}>
                      Manage devices and locations where you're currently logged in.
                    </p>
                  </div>
                </div>

                <div className={styles.sessionInfo}>
                  <p>
                    You have <strong>{settings?.active_sessions_count || 1}</strong> active session(s).
                  </p>
                  <Button kind="tertiary" size="md" renderIcon={Events} onClick={() => navigate('/security/sessions')}>
                    View All Sessions
                  </Button>
                </div>
              </Stack>
            </Tile>

            {/* Password Management */}
            <Tile className={styles.securitySection}>
              <Stack gap={5}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionIcon}>
                    <Password size={24} />
                  </div>
                  <div>
                    <h3 className={styles.sectionHeading}>Password</h3>
                    <p className={styles.sectionDescription}>Keep your password strong and change it regularly.</p>
                  </div>
                </div>

                <div className={styles.passwordInfo}>
                  <p>
                    Last changed:{' '}
                    <strong>
                      {settings?.password_last_changed
                        ? new Date(settings.password_last_changed).toLocaleDateString()
                        : 'Unknown'}
                    </strong>
                  </p>
                  <Button kind="tertiary" size="md" renderIcon={Password}>
                    Change Password
                  </Button>
                </div>
              </Stack>
            </Tile>
          </Stack>
        </Column>
      </Grid>

      {/* MFA Setup Modal */}
      <MFASetup
        isOpen={mfaSetupOpen}
        onClose={() => setMfaSetupOpen(false)}
        onComplete={handleMfaSetupComplete}
        userId={userId}
        userEmail={userEmail}
      />
    </Content>
  );
};

export default SecuritySettingsPage;
